import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["step", "overlay", "tooltip", "progress"]
  static values = { 
    currentStep: Number,
    totalSteps: Number,
    autoStart: Boolean,
    skipable: Boolean
  }

  connect() {
    this.initializeTour()
    
    if (this.autoStartValue) {
      this.startTour()
    }
  }

  initializeTour() {
    this.steps = [
      {
        target: '[data-report-builder-target="dataSource"]',
        title: 'Select Your Data Source',
        content: 'Start by choosing which data you want to include in your report. This determines what fields and information will be available.',
        position: 'bottom',
        action: 'highlight'
      },
      {
        target: '.field-picker-container',
        title: 'Choose Your Fields',
        content: 'Select the specific data fields you want to include in your report. You can search for fields or browse by category.',
        position: 'right',
        action: 'highlight'
      },
      {
        target: '.selected-fields-container',
        title: 'Configure Selected Fields',
        content: 'Customize how your selected fields appear in the report. You can reorder them by dragging or use the Configure button for advanced options.',
        position: 'left',
        action: 'highlight'
      },
      {
        target: '.filter-container',
        title: 'Add Filters (Optional)',
        content: 'Narrow down your data by adding filters. This helps you focus on specific information that matters to you.',
        position: 'top',
        action: 'highlight'
      },
      {
        target: '.chart-builder-section',
        title: 'Create Charts (Optional)',
        content: 'Visualize your data with charts and graphs. Choose from different chart types and customize their appearance.',
        position: 'top',
        action: 'highlight'
      },
      {
        target: '.preview-section',
        title: 'Preview and Export',
        content: 'See a preview of your report and export it in various formats like PDF, Excel, or CSV. You can also save your configuration as a template for future use.',
        position: 'top',
        action: 'highlight'
      }
    ]
    
    this.currentStepValue = 0
    this.totalStepsValue = this.steps.length
    this.isActive = false
    
    this.createTourElements()
  }

  createTourElements() {
    // Create overlay
    this.overlay = document.createElement('div')
    this.overlay.className = 'onboarding-overlay'
    this.overlay.innerHTML = `
      <div class="onboarding-spotlight"></div>
    `
    
    // Create tooltip
    this.tooltip = document.createElement('div')
    this.tooltip.className = 'onboarding-tooltip'
    this.tooltip.innerHTML = `
      <div class="tooltip-header">
        <h5 class="tooltip-title"></h5>
        <button type="button" class="btn-close" aria-label="Close tour"></button>
      </div>
      <div class="tooltip-body">
        <p class="tooltip-content"></p>
      </div>
      <div class="tooltip-footer">
        <div class="progress-container">
          <div class="progress">
            <div class="progress-bar" role="progressbar"></div>
          </div>
          <small class="progress-text">Step <span class="current-step">1</span> of <span class="total-steps">${this.totalStepsValue}</span></small>
        </div>
        <div class="button-group">
          <button type="button" class="btn btn-outline-secondary btn-skip">Skip Tour</button>
          <button type="button" class="btn btn-outline-primary btn-previous" disabled>Previous</button>
          <button type="button" class="btn btn-primary btn-next">Next</button>
        </div>
      </div>
    `
    
    // Add event listeners
    this.setupEventListeners()
  }

  setupEventListeners() {
    // Close button
    this.tooltip.querySelector('.btn-close').addEventListener('click', () => {
      this.endTour()
    })
    
    // Skip button
    this.tooltip.querySelector('.btn-skip').addEventListener('click', () => {
      this.skipTour()
    })
    
    // Previous button
    this.tooltip.querySelector('.btn-previous').addEventListener('click', () => {
      this.previousStep()
    })
    
    // Next button
    this.tooltip.querySelector('.btn-next').addEventListener('click', () => {
      this.nextStep()
    })
    
    // Keyboard navigation
    document.addEventListener('keydown', (event) => {
      if (!this.isActive) return
      
      switch (event.key) {
        case 'Escape':
          this.endTour()
          break
        case 'ArrowLeft':
          event.preventDefault()
          this.previousStep()
          break
        case 'ArrowRight':
          event.preventDefault()
          this.nextStep()
          break
      }
    })
    
    // Click outside to close (optional)
    this.overlay.addEventListener('click', (event) => {
      if (event.target === this.overlay) {
        this.endTour()
      }
    })
  }

  startTour() {
    if (this.isActive) return
    
    this.isActive = true
    this.currentStepValue = 0
    
    // Add elements to DOM
    document.body.appendChild(this.overlay)
    document.body.appendChild(this.tooltip)
    
    // Add body class for styling
    document.body.classList.add('onboarding-active')
    
    // Show first step
    this.showStep(0)
    
    // Announce to screen readers
    this.announceToScreenReader('Interactive tour started. Use arrow keys to navigate, Escape to exit.')
  }

  showStep(stepIndex) {
    if (stepIndex < 0 || stepIndex >= this.steps.length) return
    
    const step = this.steps[stepIndex]
    const targetElement = document.querySelector(step.target)
    
    if (!targetElement) {
      console.warn(`Tour step target not found: ${step.target}`)
      this.nextStep()
      return
    }
    
    // Update current step
    this.currentStepValue = stepIndex
    
    // Highlight target element
    this.highlightElement(targetElement)
    
    // Position and show tooltip
    this.positionTooltip(targetElement, step)
    
    // Update tooltip content
    this.updateTooltipContent(step)
    
    // Update navigation buttons
    this.updateNavigationButtons()
    
    // Update progress
    this.updateProgress()
    
    // Scroll element into view
    this.scrollToElement(targetElement)
    
    // Announce step to screen readers
    this.announceToScreenReader(`Step ${stepIndex + 1} of ${this.totalStepsValue}: ${step.title}. ${step.content}`)
  }

  highlightElement(element) {
    // Remove previous highlights
    document.querySelectorAll('.onboarding-highlight').forEach(el => {
      el.classList.remove('onboarding-highlight')
    })
    
    // Add highlight to current element
    element.classList.add('onboarding-highlight')
    
    // Position spotlight
    const rect = element.getBoundingClientRect()
    const spotlight = this.overlay.querySelector('.onboarding-spotlight')
    
    spotlight.style.left = `${rect.left - 10}px`
    spotlight.style.top = `${rect.top - 10}px`
    spotlight.style.width = `${rect.width + 20}px`
    spotlight.style.height = `${rect.height + 20}px`
    
    // Show overlay
    this.overlay.classList.add('active')
  }

  positionTooltip(targetElement, step) {
    const rect = targetElement.getBoundingClientRect()
    const tooltipRect = this.tooltip.getBoundingClientRect()
    const viewportWidth = window.innerWidth
    const viewportHeight = window.innerHeight
    
    let left, top
    
    // Calculate position based on preferred position
    switch (step.position) {
      case 'top':
        left = rect.left + (rect.width / 2) - (tooltipRect.width / 2)
        top = rect.top - tooltipRect.height - 20
        break
      case 'bottom':
        left = rect.left + (rect.width / 2) - (tooltipRect.width / 2)
        top = rect.bottom + 20
        break
      case 'left':
        left = rect.left - tooltipRect.width - 20
        top = rect.top + (rect.height / 2) - (tooltipRect.height / 2)
        break
      case 'right':
        left = rect.right + 20
        top = rect.top + (rect.height / 2) - (tooltipRect.height / 2)
        break
      default:
        left = rect.right + 20
        top = rect.top
    }
    
    // Adjust if tooltip would go outside viewport
    if (left < 10) left = 10
    if (left + tooltipRect.width > viewportWidth - 10) {
      left = viewportWidth - tooltipRect.width - 10
    }
    if (top < 10) top = 10
    if (top + tooltipRect.height > viewportHeight - 10) {
      top = viewportHeight - tooltipRect.height - 10
    }
    
    // Apply position
    this.tooltip.style.left = `${left}px`
    this.tooltip.style.top = `${top}px`
    
    // Add position class for arrow styling
    this.tooltip.className = `onboarding-tooltip position-${step.position}`
    
    // Show tooltip
    this.tooltip.classList.add('active')
  }

  updateTooltipContent(step) {
    this.tooltip.querySelector('.tooltip-title').textContent = step.title
    this.tooltip.querySelector('.tooltip-content').textContent = step.content
  }

  updateNavigationButtons() {
    const previousBtn = this.tooltip.querySelector('.btn-previous')
    const nextBtn = this.tooltip.querySelector('.btn-next')
    
    // Update previous button
    previousBtn.disabled = this.currentStepValue === 0
    
    // Update next button text
    if (this.currentStepValue === this.totalStepsValue - 1) {
      nextBtn.textContent = 'Finish'
      nextBtn.classList.remove('btn-primary')
      nextBtn.classList.add('btn-success')
    } else {
      nextBtn.textContent = 'Next'
      nextBtn.classList.remove('btn-success')
      nextBtn.classList.add('btn-primary')
    }
  }

  updateProgress() {
    const progress = ((this.currentStepValue + 1) / this.totalStepsValue) * 100
    const progressBar = this.tooltip.querySelector('.progress-bar')
    const currentStepSpan = this.tooltip.querySelector('.current-step')
    
    progressBar.style.width = `${progress}%`
    progressBar.setAttribute('aria-valuenow', progress)
    currentStepSpan.textContent = this.currentStepValue + 1
  }

  scrollToElement(element) {
    // Smooth scroll to element with some padding
    const rect = element.getBoundingClientRect()
    const absoluteTop = window.pageYOffset + rect.top
    const middle = absoluteTop - (window.innerHeight / 2) + (rect.height / 2)
    
    window.scrollTo({
      top: middle,
      behavior: 'smooth'
    })
  }

  nextStep() {
    if (this.currentStepValue < this.totalStepsValue - 1) {
      this.showStep(this.currentStepValue + 1)
    } else {
      this.completeTour()
    }
  }

  previousStep() {
    if (this.currentStepValue > 0) {
      this.showStep(this.currentStepValue - 1)
    }
  }

  skipTour() {
    this.announceToScreenReader('Tour skipped')
    this.endTour()
    
    // Track skip event
    this.trackTourEvent('skipped', this.currentStepValue)
  }

  completeTour() {
    this.announceToScreenReader('Tour completed successfully')
    this.endTour()
    
    // Show completion message
    this.showCompletionMessage()
    
    // Track completion event
    this.trackTourEvent('completed', this.totalStepsValue)
  }

  endTour() {
    if (!this.isActive) return
    
    this.isActive = false
    
    // Remove highlights
    document.querySelectorAll('.onboarding-highlight').forEach(el => {
      el.classList.remove('onboarding-highlight')
    })
    
    // Remove elements from DOM
    if (this.overlay.parentNode) {
      this.overlay.parentNode.removeChild(this.overlay)
    }
    if (this.tooltip.parentNode) {
      this.tooltip.parentNode.removeChild(this.tooltip)
    }
    
    // Remove body class
    document.body.classList.remove('onboarding-active')
    
    // Store completion status
    localStorage.setItem('reportBuilderTourCompleted', 'true')
  }

  showCompletionMessage() {
    // Create temporary success message
    const message = document.createElement('div')
    message.className = 'alert alert-success alert-dismissible fade show position-fixed'
    message.style.cssText = `
      top: 20px;
      left: 50%;
      transform: translateX(-50%);
      z-index: 9999;
      min-width: 300px;
      text-align: center;
    `
    message.innerHTML = `
      <i class="fas fa-check-circle me-2"></i>
      <strong>Tour Complete!</strong> You're ready to build amazing reports.
      <button type="button" class="btn-close" data-bs-dismiss="alert"></button>
    `
    
    document.body.appendChild(message)
    
    // Auto-remove after 5 seconds
    setTimeout(() => {
      if (message.parentNode) {
        message.remove()
      }
    }, 5000)
  }

  // Public methods for external control
  restart() {
    this.endTour()
    setTimeout(() => {
      this.startTour()
    }, 100)
  }

  goToStep(stepIndex) {
    if (this.isActive && stepIndex >= 0 && stepIndex < this.totalStepsValue) {
      this.showStep(stepIndex)
    }
  }

  // Utility methods
  announceToScreenReader(message) {
    // Create or update announcement region
    let announcer = document.getElementById('tour-announcer')
    if (!announcer) {
      announcer = document.createElement('div')
      announcer.id = 'tour-announcer'
      announcer.setAttribute('aria-live', 'polite')
      announcer.setAttribute('aria-atomic', 'true')
      announcer.className = 'sr-only'
      document.body.appendChild(announcer)
    }
    
    announcer.textContent = message
  }

  trackTourEvent(action, step) {
    // Track tour events for analytics (implement based on your analytics setup)
    if (typeof gtag !== 'undefined') {
      gtag('event', 'onboarding_tour', {
        action: action,
        step: step,
        total_steps: this.totalStepsValue
      })
    }
    
  }

  // Check if user has completed tour before
  static shouldShowTour() {
    return !localStorage.getItem('reportBuilderTourCompleted')
  }

  // Reset tour completion status (for testing or re-onboarding)
  static resetTourStatus() {
    localStorage.removeItem('reportBuilderTourCompleted')
  }
}